import { useState, useReducer, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import cncLogo from 'images/MyCast&CrewLogo.svg';
import { useMediaQuery } from 'react-responsive';

import Loader from 'components/Loader';
import TermsAndConditions from './modals/TermsAndConditions';
import Typography from 'components/CncUI/Typography';
import Stepper, { Step } from 'components/CncUI/Stepper';

import Welcome from './steps/Welcome';
import FindAccount from './steps/FindAccount';
import SearchAccount from './steps/SearchAccount';
import VerifyAddress from './steps/VerifyAddress';
import InOurSystem from './steps/InOurSystem';
import ThankYou from './steps/ThankYou';
import Affidavit from './steps/Affidavit';
import Error from 'pages/OfferRegistration/steps/Error';

import withPayrollStatusProtection from 'hoc/withPayrollStatusProtection';
import useCheckAuthentication from 'hooks/useCheckAuthentication';
import loadRecaptcha from 'utilities/loadRecaptcha';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  toolbar: {
    [breakpoints.up('md')]: {
      paddingLeft: 50,
    },
  },
  header: {
    background: palette.cncUI.gradients.header,
    boxShadow: 'none',
  },
  logo: {
    flexGrow: 1,
    textAlign: 'center',
    [breakpoints.down('sm')]: {
      marginTop: 27,
      marginBottom: 23,
    },
    [breakpoints.up('md')]: {
      textAlign: 'left',
    },
    '& img': {
      width: 154,
      marginTop: 2,
    },
  },
  stepper: {
    paddingTop: 0,
    [breakpoints.up('md')]: {
      padding: 0,
    },
  },
}));

const PROGRESS = 'progress';
const COMPLETED = 'completed';

const Register = () => {
  const logger = Logger('Register');
  const classes = useStyles();
  const [step, setStep] = useState('Welcome');
  const reducer = (state, data) => ({ ...state, ...data });
  const [state, changeState] = useReducer(reducer, { fingerprint: null });
  const [terms, showTerms] = useState(false);
  const [status, setStatus] = useState(PROGRESS);
  const isMobile = useMediaQuery({
    query: '(max-width: 960px)',
  });

  useEffect(() => {
    if (step === 'Error' && state.error) {
      // We want to record the step where the problem happened,
      // but to logic at this point its already the "prevStep",
      // with current being 'Error'
      logger.error(state.error);
    }
  }, [step]);

  useCheckAuthentication(token => {
    if (!token) {
      showTerms(true);
    } else {
      const { email } = token.claims || {};
      changeState({ lockedEmail: email });
    }
    setStatus(COMPLETED);
    loadRecaptcha();
  });

  if (status === PROGRESS) {
    return <Loader />;
  }

  const steps = {
    Welcome,
    FindAccount,
    SearchAccount,
    VerifyAddress,
    InOurSystem,
    Affidavit,
    ThankYou,
    Error,
  };
  const Component = steps[step];

  const stepOptions = {
    Welcome: -1,
    FindAccount: 0,
    SearchAccount: 0,
    VerifyAddress: 1,
    Affidavit: 2,
    InOurSystem: -1,
    ThankYou: 3,
    Error: -1,
  };
  const activeStep = stepOptions[step];

  return (
    <div className={classes.root}>
      <TermsAndConditions
        show={terms}
        onClose={() => showTerms(false)}
        changeState={changeState}
      />
      {!['Welcome', 'InOurSystem'].includes(step) && (
        <AppBar position="static" className={classes.header}>
          <Toolbar className={classes.toolbar}>
            <Grid container spacing={0} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography
                  variant="h6"
                  color="inherit"
                  className={classes.logo}
                >
                  <img alt="logo" align="center" src={cncLogo} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                {activeStep > -1 && (
                  <Stepper
                    light
                    short={isMobile}
                    activeStep={activeStep}
                    className={classes.stepper}
                  >
                    <Step label="Find Account" />
                    <Step label="Confirm Identity" />
                    <Step label="Complete" />
                  </Stepper>
                )}
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      )}
      <Component
        state={state}
        changeState={changeState}
        setStep={setStep}
        isMobile={isMobile}
        canadaEnabled={true}
      />
    </div>
  );
};

export default withPayrollStatusProtection()(Register);
